import React, { useState, useEffect, useRef } from 'react';
import { gsap } from 'gsap';
import './ClientType.scss';

import { useGetOnboardingMasterData } from 'src/api/mutations/useGetOnboardingMasterData';
import { useSaveOnboardingStep } from 'src/api/mutations/useSaveOnboardingStep';
import { sessionStore } from 'src/store/sessionStore';
import { getUserIdFromToken } from 'src/utils/authUtils';
import { useGetOnboardingState } from 'src/api/mutations/useGetOnboardingState';
import toast from 'src/utils/appAlert';
import { ReactComponent as Health_mobility_lifestyleIcon } from '../../../../assets/images/onboarding-trainer/client-type/health_mobility_lifestyle.svg';
import { ReactComponent as Performance__strengthIcon } from '../../../../assets/images/onboarding-trainer/client-type/performance__strength.svg';
import { ReactComponent as Body_transformationIcon } from '../../../../assets/images/onboarding-trainer/client-type/body_transformation.svg';
import { ReactComponent as General_fitnessIcon } from '../../../../assets/images/onboarding-trainer/client-type/general_fitness.svg';
import { ReactComponent as Goal_specificIcon } from '../../../../assets/images/onboarding-trainer/client-type/goal_specific.svg';
import { ReactComponent as BeginnerIcon } from '../../../../assets/images/onboarding-trainer/client-type/beginner.svg';

const ICON_MAP: Record<string, any> = {
  'goal-specific': <Goal_specificIcon />,
  beginner: <BeginnerIcon />,
  'general-fitness': <General_fitnessIcon />,
  'body-transformation': <Body_transformationIcon />,
  'performance-strength': <Performance__strengthIcon />,
  'health-mobility': <Health_mobility_lifestyleIcon />,
};

interface ClientTypeProps {
  onNext?: (selectedIds: string[]) => void;
  onBack?: () => void;
}

export const ClientType: React.FC<ClientTypeProps> = ({ onNext, onBack }) => {
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const cardsRef = useRef<(HTMLDivElement | null)[]>([]);

  const { data: masterData } = useGetOnboardingMasterData();
  const { mutate: saveStep, isPending } = useSaveOnboardingStep();

  const token = sessionStore((s) => s.token);
  const userId = getUserIdFromToken(token);

  const clientTypeData = masterData?.['onboarding_client_type'];
  const options = clientTypeData?.options || [];

  // Mobile specific title based on the screenshot
  const titleText = clientTypeData?.title || 'What type of clients are you looking for?';

  const { data: stateData } = useGetOnboardingState(userId || undefined);

  useEffect(() => {
    if (stateData?.onboarding?.step2_client_types && selectedIds.length === 0) {
      setSelectedIds(stateData.onboarding.step2_client_types);
    }
  }, [selectedIds.length, stateData?.onboarding.step2_client_types]);

  useEffect(() => {
    if (cardsRef.current.length > 0) {
      gsap.fromTo(
        cardsRef.current,
        { opacity: 0, y: 25 },
        {
          opacity: 1,
          y: 0,
          duration: 0.85,
          stagger: 0.15,
          ease: 'power3.out',
        },
      );
    }
  }, [options.length]);

  const handleToggle = (id: string) => {
    if (selectedIds.includes(id)) {
      setSelectedIds(selectedIds.filter((i) => i !== id));
    } else {
      if (selectedIds.length >= 3) {
        toast.error('You can select a maximum of 3 client types.');
        return;
      }
      setSelectedIds([...selectedIds, id]);
    }
  };

  const handleNext = () => {
    if (selectedIds.length > 0) {
      if (userId) {
        saveStep({ uid: userId, step: 2, data: selectedIds });
      }
      if (onNext) onNext(selectedIds);
    }
  };

  return (
    <div className="client-type-container">
      <div className="client-type-header">
        <h1 className="title">{titleText}</h1>
        <p className="subtitle">{clientTypeData?.subtitle || 'Select all that apply'}</p>
      </div>

      <div className="client-type-grid">
        {options.map((option, index) => {
          const isSelected = selectedIds.includes(option.slug);
          return (
            <div
              key={option.slug}
              ref={(el) => {
                cardsRef.current[index] = el;
              }}
              className={`client-type-card ${isSelected ? 'selected' : ''}`}
              onClick={() => handleToggle(option.slug)}
              style={{ opacity: 0 }}
            >
              <div className="card-header">
                <div className="card-header-icon">{ICON_MAP[option.slug]}</div>
                <h3>{option.title}</h3>
              </div>
              <div className="radio-circle">
                {isSelected && (
                  <svg
                    width="14"
                    height="14"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="#000"
                    strokeWidth="4"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <polyline points="20 6 9 17 4 12" />
                  </svg>
                )}
              </div>
              <div className="card-content">
                <p>{option.description}</p>
                <ul className="feature-list">
                  {(option.bullets || []).map((feature, i) => (
                    <li key={i}>
                      <span className="check-icon">
                        <svg
                          width="12"
                          height="10"
                          viewBox="0 0 12 10"
                          fill="none"
                          xmlns="http://www.w3.org/2000/svg"
                        >
                          <path
                            d="M1 5L4.5 8.5L11 1"
                            stroke="#fff"
                            strokeWidth="1.5"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                        </svg>
                      </span>
                      {feature}
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          );
        })}
      </div>

      <div className="client-type-footer">
        <div className="progress-container">
          <div className="progress-bar">
            <div className="progress-fill" style={{ width: '66%' }}></div>
          </div>
        </div>
        <div className="footer-actions">
          <div className="back-btn-wrapper">
            <button className="btn-back" onClick={onBack} disabled={isPending}>
              <svg
                width="20"
                height="20"
                viewBox="0 0 24 24"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M19 12H5M5 12L12 19M5 12L12 5"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
              Back
            </button>
          </div>
          <div className="next-btn-wrapper">
            <button
              className={`btn-next ${selectedIds.length > 0 ? 'active' : ''}`}
              onClick={handleNext}
              disabled={selectedIds.length === 0}
            >
              Next
              <svg
                width="20"
                height="20"
                viewBox="0 0 24 24"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M5 12H19M19 12L12 5M19 12L12 19"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};
